import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { TrackingClient } from "@sparefoot/tracking-client";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";
import { FilterDropdown, FilterPanel } from "components/organisms";
import { FILTER_GROUPS } from "config/filters";
import FilterIcon from "assets/icons/filter.inline.svg";
import SizeGuideIcon from "assets/icons/size-guide.inline.svg";
import NoResults from "assets/icons/no-results.inline.svg";
import Modal from "components/molecules/Modal/Modal";
import StorageUnitSizeModal from "components/molecules/StorageUnitSizeModal/StorageUnitSizeModal";
import UnitCardV2 from "../UnitCardV2/UnitCardV2";

import "./UnitsListV2.scss";

const FILTER_STORAGE_KEY_PREFIX = "unit_filters_";

const UnitsListV2 = ({
	units,
	facilityId,
	facilityName,
	location,
	cityUrl
}) => {
	const [selectedSizes, setSelectedSizes] = useState([]);
	const [selectedAmenities, setSelectedAmenities] = useState([]);
	const [openDropdown, setOpenDropdown] = useState(null);
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
	const [expandedCardId, setExpandedCardId] = useState(null);
	const [lockedHeights, setLockedHeights] = useState({});
	const cardRefs = useRef({});
	const gridRef = useRef(null);
	const rafRef = useRef(null);
	const hasHydratedRef = useRef(false);

	const storageKey = `${FILTER_STORAGE_KEY_PREFIX}${facilityId}`;

	// Get search filters from Redux state
	const searchFilters = useSelector((state) => state.search.searchFilters);
	const sizeOptions = searchFilters?.[FILTER_GROUPS.UNIT_SIZE]?.options || {};
	const amenityOptions = searchFilters?.[FILTER_GROUPS.AMENITIES]?.options || {};

	const optionsLoaded = Object.keys(sizeOptions).length > 0 || Object.keys(amenityOptions).length > 0;

	useEffect(() => {
		if (!optionsLoaded || hasHydratedRef.current) return;
		try {
			const stored = JSON.parse(sessionStorage.getItem(storageKey));
			if (stored?.sizes?.length) {
				const validSizes = stored.sizes.filter((s) => s in sizeOptions);
				if (validSizes.length) setSelectedSizes(validSizes);
			}
			if (stored?.amenities?.length) {
				const validAmenities = stored.amenities.filter((a) => a in amenityOptions);
				if (validAmenities.length) setSelectedAmenities(validAmenities);
			}
		} catch { /* ignore */ }
		hasHydratedRef.current = true;
	}, [storageKey, sizeOptions, amenityOptions, optionsLoaded]);

	// Create value-to-label mapping for amenities (unit.availableAmenities contains labels)
	const amenityValueToLabel = useMemo(
		() =>
			Object.values(amenityOptions).reduce((acc, opt) => {
				acc[opt.value] = opt.label;
				return acc;
			}, {}),
		[amenityOptions]
	);

	const filteredUnits = useMemo(() => {
		const filtered = units.filter((unit) => {
			// Size filter - match by sqft range
			const sizeMatch =
				selectedSizes.length === 0 ||
				selectedSizes.some((sizeRange) => {
					const [min, max] = sizeRange.split("-").map(Number);
					return (
						unit.squareFootage >= min && unit.squareFootage <= max
					);
				});

			// Amenity filter - convert selected values to labels for comparison
			const amenityMatch =
				selectedAmenities.length === 0 ||
				selectedAmenities.some((amenity) => {
					const label = amenityValueToLabel[amenity];
					return (unit.availableAmenities || []).includes(label);
				});

			return sizeMatch && amenityMatch;
		});

		return filtered;
	}, [units, selectedSizes, selectedAmenities, amenityValueToLabel]);

	const getColumnsCount = useCallback(() => {
		if (!gridRef.current) return 1;
		const gridStyle = window.getComputedStyle(gridRef.current);
		return gridStyle.getPropertyValue("grid-template-columns").split(" ").length;
	}, []);

	const handleToggleExpand = useCallback((cardId) => {
		if (expandedCardId === cardId) {
			// Collapsing the same card
			setLockedHeights({});
			setExpandedCardId(null);
			return;
		}

		setLockedHeights({});
		setExpandedCardId(null);

		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		rafRef.current = requestAnimationFrame(() => {
			const columns = getColumnsCount();

			if (columns <= 1) {
				setExpandedCardId(cardId);
				return;
			}

			const cardIds = filteredUnits.map((u) => u.id);
			const cardIndex = cardIds.indexOf(cardId);
			const rowStart = Math.floor(cardIndex / columns) * columns;
			const rowEnd = Math.min(rowStart + columns, cardIds.length);
			const newLockedHeights = {};

			for (let i = rowStart; i < rowEnd; i+=1) {
				const id = cardIds[i];
				const el = cardRefs.current[id];
				if (el && id !== cardId) {
					newLockedHeights[id] = el.getBoundingClientRect().height;
				}
			}

			setLockedHeights(newLockedHeights);
			setExpandedCardId(cardId);
		});
	}, [expandedCardId, filteredUnits, getColumnsCount]);

	// Persist filters to sessionStorage so they survive navigation
	useEffect(() => {
		if (!hasHydratedRef.current) return;
		sessionStorage.setItem(storageKey, JSON.stringify({
			sizes: selectedSizes,
			amenities: selectedAmenities
		}));
	}, [selectedSizes, selectedAmenities, storageKey]);

	// Reset expansion when filters/sort change
	useEffect(() => {
		setExpandedCardId(null);
		setLockedHeights({});
	}, [selectedSizes, selectedAmenities]);

	// Clean up rAF on unmount
	useEffect(() => () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		}, []);

	// Reset locked heights on window resize (debounced)
	useEffect(() => {
		let resizeTimer;
		const handleResize = () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				setExpandedCardId(null);
				setLockedHeights({});
			}, 150);
		};
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(resizeTimer);
		};
	}, []);

	const handleReserve = useCallback(
		(unit) => {
			TrackingClient.trackFacilityUnitSelected({
				facility: {
					id: unit?.facilityId || facilityId,
					name: facilityName,
					location: location?.zip
				},
				last_unit_added_date: new Date(),
				unit: {
					currency: "usd",
					id: unit?.id,
					variant_size: unit?.sizeText,
					price: unit?.lowestPrice,
					quantity: 1
				},
				unit_id: unit?.id,
				unit_promo: unit?.special
			});
			trackCTAClick(
				"render-reservation-form-button",
				"Continue Now Button",
				"Reserve Now",
				cityUrl
			);
		},
		[facilityId, facilityName, location?.zip, cityUrl]
	);

	const toggleSizeOption = useCallback((size) => {
		setSelectedSizes((prev) =>
			prev.includes(size)
				? prev.filter((s) => s !== size)
				: [...prev, size]
		);
	}, []);

	const toggleAmenityOption = useCallback((amenity) => {
		setSelectedAmenities((prev) =>
			prev.includes(amenity)
				? prev.filter((a) => a !== amenity)
				: [...prev, amenity]
		);
	}, []);

	const clearSizes = useCallback(() => {
		setSelectedSizes([]);
	}, []);

	const selectAllSizes = useCallback(() => {
		setSelectedSizes(Object.keys(sizeOptions));
	}, [sizeOptions]);

	const clearAmenities = useCallback(() => {
		setSelectedAmenities([]);
	}, []);

	const selectAllAmenities = useCallback(() => {
		setSelectedAmenities(Object.keys(amenityOptions));
	}, [amenityOptions]);

	const toggleSizeDropdown = useCallback((isOpen) => {
		setOpenDropdown(isOpen ? FILTER_GROUPS.UNIT_SIZE : null);
	}, []);

	const toggleAmenitiesDropdown = useCallback((isOpen) => {
		setOpenDropdown(isOpen ? FILTER_GROUPS.AMENITIES : null);
	}, []);

	const clearAllFilters = useCallback(() => {
		setSelectedSizes([]);
		setSelectedAmenities([]);
	}, []);

	const selectAllFilters = useCallback(() => {
		setSelectedSizes(Object.keys(sizeOptions));
		setSelectedAmenities(Object.keys(amenityOptions));
	}, [sizeOptions, amenityOptions]);

	const toggleMobileFilters = useCallback((isOpen) => {
		setMobileFiltersOpen(isOpen);
	}, []);

	const applyMobileFilters = useCallback(() => {
		setMobileFiltersOpen(false);
	}, []);

	const totalFilterCount = selectedSizes.length + selectedAmenities.length;
	const SIZE_GUIDE_URL = "/self-storage-size-guide";

	// Filter sections configuration for mobile FilterPanel
	const filterSections = useMemo(
		() => [
			{
				id: FILTER_GROUPS.UNIT_SIZE,
				title: "Size",
				options: sizeOptions,
				selectedOptions: selectedSizes,
				onToggleOption: toggleSizeOption,
				footer: (
					<button
						type="button"
						className="learn-sizes-link"
						onClick={() => setIsSizeModalOpen(true)}
						aria-haspopup="dialog"
					>
						Learn more about storage sizes
					</button>
				)
			},
			{
				id: FILTER_GROUPS.AMENITIES,
				title: "Amenities",
				options: amenityOptions,
				selectedOptions: selectedAmenities,
				onToggleOption: toggleAmenityOption,
				footer: <a href={SIZE_GUIDE_URL}>How much space do you need?</a>
			}
		],
		[
			sizeOptions,
			selectedSizes,
			toggleSizeOption,
			amenityOptions,
			selectedAmenities,
			toggleAmenityOption
		]
	);
	if (!units.length) return null;

	return (
		<div className="units-list-v2-container">
			
			{/* Header with filters */}
			<Modal
				isOpen={isSizeModalOpen}
				closable
				size={Modal.SIZES.LARGE}
				handleClose={() => setIsSizeModalOpen(false)}
				toolbarContent="Storage Unit Sizes"
				className="unit-size-modal"
			>
				<div id="unit-size-modal-content">
					<StorageUnitSizeModal />
				</div>
			</Modal>
			<div className="units-header">
				<h2 className="units-title">
					Available Storage Units{" "}
					<span className="units-count">
						({filteredUnits.length})
					</span>
				</h2>

				{/* Desktop filters */}
				<div className="units-filters">
					<FilterDropdown
						label="Size"
						icon={SizeGuideIcon}
						options={sizeOptions}
						selectedOptions={selectedSizes}
						onToggleOption={toggleSizeOption}
						onClearAll={clearSizes}
						onSelectAll={selectAllSizes}
						isOpen={openDropdown === FILTER_GROUPS.UNIT_SIZE}
						onToggle={toggleSizeDropdown}
						footer={
							<button
								type="button"
								className="learn-sizes-link"
								onClick={() => setIsSizeModalOpen(true)}
								aria-haspopup="dialog"
							>
								Learn more about storage sizes
							</button>
						}
					/>

					<FilterDropdown
						label="Amenities"
						icon={FilterIcon}
						options={amenityOptions}
						selectedOptions={selectedAmenities}
						onToggleOption={toggleAmenityOption}
						onClearAll={clearAmenities}
						onSelectAll={selectAllAmenities}
						isOpen={openDropdown === FILTER_GROUPS.AMENITIES}
						onToggle={toggleAmenitiesDropdown}
						footer={
							<a href={SIZE_GUIDE_URL}>
								How much space do you need?
							</a>
						}
					/>

				</div>

				{/* Mobile filters */}
				<FilterPanel
					isOpen={mobileFiltersOpen}
					onToggle={toggleMobileFilters}
					title="Available Units"
					subtitle={`(${filteredUnits.length})`}
					sections={filterSections}
					onClearAll={clearAllFilters}
					onSelectAll={selectAllFilters}
					onApply={applyMobileFilters}
					label="Filters"
					icon={FilterIcon}
					selectedCount={totalFilterCount}
				/>
			</div>

			{/* Unit cards grid */}
			{filteredUnits.length ? (
				<div className="units-list-v2" ref={gridRef}>
					{filteredUnits.map((u) => (
						<UnitCardV2
							key={u.id}
							unit={u}
							onReserve={handleReserve}
							expanded={expandedCardId === u.id}
							onToggleExpand={handleToggleExpand}
							cardRef={(el) => { cardRefs.current[u.id] = el; }}
							style={lockedHeights[u.id] ? { height: `${lockedHeights[u.id]  }px`, overflow: "hidden" } : undefined}
						/>
					))}
				</div>
			) : (
				<div className="no-results">
					<div className="no-results-content">
						<NoResults className="no-results-icon" />
						<div className="no-results-text">
							<h3 className="no-results-title">
								No results found
							</h3>
							<p className="no-results-subtitle">
								Please expand your filter selections to see more
								units
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

UnitsListV2.propTypes = {
	units: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			sizeText: PropTypes.string,
			unitType: PropTypes.string,
			availableAmenities: PropTypes.arrayOf(PropTypes.string),
			special: PropTypes.string,
			lowestPrice: PropTypes.number,
			startingPrice: PropTypes.number,
			quantity: PropTypes.number,
			squareFootage: PropTypes.number
		})
	).isRequired,
	facilityId: PropTypes.number,
	facilityName: PropTypes.string,
	location: PropTypes.object,
	cityUrl: PropTypes.string
};

UnitsListV2.defaultProps = {
	facilityId: null,
	facilityName: "",
	location: null,
	cityUrl: ""
};

export default UnitsListV2;
